import { useEffect, useState } from 'react';
import RegUIClient from 'reg-ui-client';

import { isProduction, isStaging } from '../utils/utils';

// Public keys
const GRECAPTCHA_KEY = {
  DEV: '6Ldg36IUAAAAAANOMM57fJ3nAH2R7Cq5cVe8lcoL',
  STAGING: '6LdehaMUAAAAAKnkPQ7strzRJtkV0z262iwF5J9D',
  PROD: '6Lfj7akUAAAAAGDt7CiiLizYUN6Syh72hVmontj7',
};

export const getGrecaptchaKey = () =>
  // eslint-disable-next-line no-nested-ternary
  isProduction()
    ? GRECAPTCHA_KEY.PROD
    : isStaging()
    ? GRECAPTCHA_KEY.STAGING
    : GRECAPTCHA_KEY.DEV;

export const getSkipRecaptchaParameter = (
  isMounted: boolean
): string | undefined => {
  if (!isMounted) {
    return undefined;
  }

  const queryParams = new URLSearchParams(window.location.search);

  return queryParams.get('skipGrecaptcha') || undefined;
};

const isRecaptchaRequiredLocal = (isMounted: boolean) => {
  return isProduction() || getSkipRecaptchaParameter(isMounted) !== 'true';
};

async function isRecaptchaRequired(
  isMounted: boolean,
  email: string,
  regUIClient?: RegUIClient
): Promise<boolean> {
  if (!isMounted) {
    // eslint-disable-next-line no-console
    console.error("isRecaptchaRequired: the component isn't mounted");

    return true;
  }

  if (!isRecaptchaRequiredLocal(isMounted)) {
    return false;
  }

  if (!regUIClient) {
    // eslint-disable-next-line no-console
    console.error('isRecaptchaRequired: no regUIClient available');

    return true;
  }

  const skipGrecaptchaParameter = getSkipRecaptchaParameter(isMounted);

  if (skipGrecaptchaParameter && skipGrecaptchaParameter !== 'true') {
    try {
      const result = await regUIClient.validateCaptchaKey(
        skipGrecaptchaParameter,
        email
      );

      if (!result.data?.valid) {
        // eslint-disable-next-line no-console
        console.error(
          `Skip Recaptcha JWT is invalid: ${result.data?.errorMessage}`
        );

        return true;
      }

      return false;
    } catch (err) {
      const error = err as Error;

      // eslint-disable-next-line no-console
      console.error(`Failed to validate JWT with error ${error.message}`);

      return true;
    }
  }

  return true;
}

export const useRecaptchaRequired = (
  isMounted: boolean,
  email: string,
  regUIClient?: RegUIClient,
  skipRecaptcha = false
): boolean => {
  const [recaptchaRequired, setRecaptchaRequired] = useState<boolean>(
    isRecaptchaRequiredLocal(isMounted)
  );

  useEffect(() => {
    if (skipRecaptcha) {
      setRecaptchaRequired(false);

      return;
    }

    isRecaptchaRequired(isMounted, email, regUIClient)
      .then((required) => {
        setRecaptchaRequired(required);
      })
      .catch((error: Error) => {
        // eslint-disable-next-line no-console
        console.error(`Failed to check recaptcha required: ${error.message}`);
      });
  }, [setRecaptchaRequired, email, skipRecaptcha]);

  return recaptchaRequired;
};
